/**
 * Tiny pub/sub bridge for the frontend measurement calculation.
 *
 * The "Calculate" button lives in the DOM sidebar (no scene access) while the
 * geometry extraction + worker run in a component inside the R3F <Canvas>.
 * This decouples the two trees without adding redux surface.
 *
 * Requests are COALESCED: on model load several unrelated sources fire the
 * trigger within a few ms (page mount, toolbar, saga, geometry-ready, auto-
 * recalc). Without coalescing each becomes a full worker run queued back-to-back
 * on the single synchronous worker — the "hang" where the same calculation runs
 * 3× in a row. A short debounce collapses a burst into one run.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

/** Coalescing window (ms) — collapses a burst of near-simultaneous requests. */
const COALESCE_MS = 120;
let coalesceTimer: ReturnType<typeof setTimeout> | null = null;

const flush = (): void => {
  coalesceTimer = null;
  console.info('[frontend-calc] requestFrontendMeasurementCalculation →', listeners.size, 'listener(s)');
  listeners.forEach((listener) => listener());
};

/** Fire a calculation request (called from the sidebar when the flag is on). */
export const requestFrontendMeasurementCalculation = (): void => {
  if (coalesceTimer) clearTimeout(coalesceTimer);
  coalesceTimer = setTimeout(flush, COALESCE_MS);
};

/** Subscribe (called by the in-Canvas runner). Returns an unsubscribe fn. */
export const onFrontendMeasurementCalculationRequest = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
